import { createGlobalStyle } from 'styled-components';
import {
  colorAccent,
  colorAdditional,
  colorBackgroundBody,
  colorBlue,
  colorBlueHover,
  colorRed,
  colorText,
  colorWhite,
  scrollbarStyles,
} from '@/utils/palette';
import { borderLeft, left, paddingLeft } from '@/utils/directions';


const GlobalStyles = createGlobalStyle`
  html {
    box-sizing: border-box;
  }

  *, *:before, *:after {
    box-sizing: inherit;
  }
  
  main {
    padding: 0;
  }

  body {
    margin: 0;
    padding: 0 !important;
    box-sizing: border-box;
    font-family: 'Roboto', sans-serif;
    font-size: 13px;
    line-height: 1.6;
    overflow-x: hidden;
  }

  .custodiy-auth-mark {
    display: block;
    width: 112px;
    height: 112px;
    margin: 14px auto 0;
    object-fit: contain;
    filter: drop-shadow(0 14px 28px rgba(124, 58, 237, 0.3));
  }

  .row > * {
    padding: 0 15px;
  }

  * {
    box-sizing: inherit;
  }

  ul, ol {
    // doesn't do RTL, it break a sidebar
    padding-left: 15px;
    margin-bottom: 0;
    list-style-image: url(${process.env.PUBLIC_URL}/img/circle.svg);
  }

  a {
    color: ${colorBlue};
    transition: all 0.3s;
    text-decoration: none;

    &:hover {
      color: ${colorBlueHover};
    }
  }

  button:focus, button:active {
    outline: none;
  }

  img {
    width: 100%;
  }

  label {
    margin-bottom: 8px;
  }

  //TYPOGRAPHY
  p, h1, h2, h3, h4, h5, h6 {
    margin-top: 0;
    font-weight: 400;
    margin-bottom: 0;
    color: ${colorText};
  }

  h1 {
    font-size: 36px;
    line-height: 48px;

    &.subhead {
      font-size: 30px;
      color: ${colorAdditional};
      line-height: 36px;
    }
  }

  h2 {
    font-size: 30px;
    line-height: 36px;

    &.subhead {
      font-size: 24px;
      color: ${colorAdditional};
      line-height: 32px;
    }
  }

  h3 {
    font-size: 24px;
    line-height: 32px;

    &.subhead {
      font-size: 18px;
      color: ${colorAdditional};
      line-height: 24px;
    }
  }

  h4 {
    font-size: 18px;
    line-height: 24px;

    &.subhead {
      font-size: 12px;
      color: ${colorAdditional};
      line-height: 16px;
    }
  }

  h5 {
    font-size: 14px;
    line-height: 18px;

    &.subhead {
      font-size: 10px;
      color: ${colorAdditional};
      line-height: 12px;
    }
  }

  h6 {
    font-size: 12px;
    line-height: 16px;

    &.subhead {
      font-size: 8px;
      color: ${colorAdditional};
      line-height: 10px;
    }
  }
  
  hr {
    opacity: 0.1;
  }
  
  .table-responsive {
    ${scrollbarStyles};
  }

  blockquote {
    font-size: 13px;
    font-style: italic;
    ${borderLeft}: 3px solid ${colorAccent};
    margin-bottom: 0;
    text-align: ${left};
    ${paddingLeft}: 10px;
  }

  .highlight {
    background-color: ${colorAccent};
    color: ${colorWhite};
  }

  .typography-message{
    text-align: ${left};
  }

  .red-text {
    color: ${colorRed};
  }
  .green-text {
    color: ${colorAccent};
  }

  .page-title {
    text-align: ${left};
    font-weight: 500;
    text-transform: capitalize;
    font-size: 20px;

    &:last-child {
      margin-bottom: 30px;
    }

    &.page-title--not-last {
      margin-top: 20px;
    }
  }

  .page-subhead {
    text-align: ${left};
    margin-bottom: 20px;

    &.subhead {
      font-size: 14px;
      opacity: 0.7;
    }
  }

  ::selection {
    color: ${colorWhite};
    background: ${colorAccent};
  }

  .first-letter-uppercase:first-letter {
    text-transform: uppercase;
  }

  .admin-page {
    --admin-panel: rgba(17, 24, 39, .82);
    --admin-panel-strong: #111827;
    --admin-border: rgba(148, 163, 184, .14);
    --admin-muted: #94a3b8;
    --admin-text: #e2e8f0;
    --admin-accent: #8b5cf6;
    width: 100%;
    min-width: 0;
    overflow-x: clip;

    .row, [class*='col-'], .card, .card-body, .container, .container-fluid {
      min-width: 0;
    }

    & > *:not(.loader-overlay) {
      max-width: 1800px;
      margin-left: auto;
      margin-right: auto;
    }

    h1, h2, h3, h4, h5, h6 { color: #f8fafc; }
    p, label { color: var(--admin-muted); }

    .container, .container-fluid {
      max-width: 1800px;
      padding-left: 28px;
      padding-right: 28px;
    }

    .card {
      color: var(--admin-text);
      border: 1px solid var(--admin-border);
      border-radius: 16px;
      background: var(--admin-panel);
      box-shadow: 0 20px 48px rgba(0, 0, 0, .16);
    }

    .card-header, .card-footer {
      border-color: var(--admin-border);
      background: transparent;
    }

    .form-control, input:not([type='checkbox']):not([type='radio']), textarea, select {
      min-height: 44px;
      color: var(--admin-text);
      border: 1px solid rgba(148, 163, 184, .2);
      border-radius: 10px;
      background-color: rgba(15, 23, 42, .72);
      box-shadow: none;
      transition: border-color .2s ease, box-shadow .2s ease;

      &:focus {
        color: #f8fafc;
        border-color: var(--admin-accent);
        background-color: rgba(15, 23, 42, .9);
        box-shadow: 0 0 0 3px rgba(139, 92, 246, .15);
      }

      &::placeholder { color: #64748b; }
    }

    .btn, button[class*='btn'] {
      min-height: 40px;
      border-radius: 10px;
      font-weight: 700;
      letter-spacing: .01em;
      transition: transform .2s ease, box-shadow .2s ease, background .2s ease;
      &:hover { transform: translateY(-1px); }
    }

    .btn-primary, .btn-info {
      border-color: transparent;
      background: linear-gradient(135deg, #7c3aed, #4f46e5);
      box-shadow: 0 10px 22px rgba(79, 70, 229, .22);
    }

    .table, table {
      width: 100%;
      margin: 0;
      color: var(--admin-text);
      border-collapse: separate;
      border-spacing: 0;
      background: transparent;
      font-variant-numeric: tabular-nums;
    }

    .admin-list-shell,
    .overflow-x-auto:has(> table),
    .table-responsive:has(> table),
    div:has(> .table),
    div:has(> table) {
      width: 100%;
      max-width: 100%;
      overflow: auto;
      border: 1px solid var(--admin-border);
      border-radius: 14px;
      background: rgba(15, 23, 42, .42);
      box-shadow: 0 16px 40px rgba(2, 6, 23, .12);
      ${scrollbarStyles};
    }

    .table thead th, table thead th {
      position: sticky;
      top: 0;
      z-index: 4;
      min-width: 112px;
      padding: 13px 18px;
      color: #a5b4cf;
      border: 0;
      border-bottom: 1px solid var(--admin-border);
      background: #161d2d !important;
      font-size: 11px;
      font-weight: 800;
      line-height: 1.3;
      letter-spacing: .075em;
      text-transform: uppercase;
      white-space: nowrap;
    }

    .table tbody td, table tbody td {
      min-width: 112px;
      padding: 13px 18px;
      color: #cbd5e1;
      border: 0;
      border-bottom: 1px solid rgba(148, 163, 184, .08);
      background: rgba(9, 14, 28, .28) !important;
      line-height: 1.35;
      vertical-align: middle;
    }

    .table tbody tr:nth-child(even) td,
    table tbody tr:nth-child(even) td {
      background: rgba(15, 23, 42, .4) !important;
    }

    .table tbody tr:last-child td,
    table tbody tr:last-child td { border-bottom: 0; }

    .table tbody tr,
    table tbody tr { transition: background .18s ease, transform .18s ease; }

    .table tbody tr:hover td,
    table tbody tr:hover td { background: rgba(99, 102, 241, .1) !important; }

    .table td:first-child, .table th:first-child,
    table td:first-child, table th:first-child { padding-left: 20px; }

    .table td:last-child, .table th:last-child,
    table td:last-child, table th:last-child { padding-right: 20px; }

    .table tbody td img,
    table tbody td img {
      width: auto;
      max-width: 44px;
      max-height: 44px;
      object-fit: cover;
    }

    .table tbody td button,
    table tbody td button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      min-width: 34px;
      min-height: 34px;
      border-radius: 9px;
    }

    .table .badge,
    table .badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-height: 30px;
      padding: 6px 11px;
      border-radius: 999px;
      font-size: 11px;
      font-weight: 800;
    }

    @media (max-width: 767px) {
      .admin-list-shell,
      .overflow-x-auto:has(> table),
      .table-responsive:has(> table),
      div:has(> .table),
      div:has(> table) {
        margin-left: -4px;
        width: calc(100% + 8px);
        border-radius: 12px;
        overscroll-behavior-inline: contain;
        -webkit-overflow-scrolling: touch;
      }

      .table, table { min-width: 760px; }
      .table thead th, table thead th,
      .table tbody td, table tbody td {
        min-width: 96px;
        padding: 11px 13px;
      }

      .container, .container-fluid {
        padding-left: 12px;
        padding-right: 12px;
      }
    }

    .modal-content {
      color: var(--admin-text);
      border: 1px solid var(--admin-border);
      border-radius: 18px;
      background: #111827;
      box-shadow: 0 32px 90px rgba(0, 0, 0, .45);
    }

    .modal-header, .modal-footer { border-color: var(--admin-border); }
    .pagination { gap: 6px; }
    .page-link {
      color: #c4b5fd;
      border: 1px solid var(--admin-border);
      border-radius: 9px !important;
      background: rgba(15, 23, 42, .72);
    }
    .page-item.active .page-link { border-color: #7c3aed; background: #7c3aed; }
  }

  .container {

    @media screen and (min-width: 768px) {
      width: 100%;
      max-width: 1630px;
    }
  }


  .rc-slider-tooltip {
    padding-bottom: 2px;

    .rc-slider-tooltip-inner {
      margin-bottom: 5px;
      background-color: ${colorBackgroundBody};
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.09);
      color: ${colorText};
      font-size: 12px;
      line-height: 12px;
      padding: 4px 4px;
      height: 20px;
      min-width: 28px;
      border-radius: 0;
    }

    &.rc-slider-tooltip-placement-top .rc-slider-tooltip-arrow {
      border-top-color: ${colorBackgroundBody};
    }

    &.rc-slider-tooltip-hidden {
      display: block;
    }
  }
`;

export default GlobalStyles;
